import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../hooks/use-api-client.js';

// Where Google's consent returns (#763): hands the code to the API, then back to the server page.

export function GoogleCallback() {
  const [params] = useSearchParams();
  const api = useApiClient();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const code = params.get('code');
    const state = params.get('state');
    if (params.get('error')) {
      setError(params.get('error') === 'access_denied' ? 'The connection was cancelled in Google.' : `Google returned an error: ${params.get('error')}.`);
      return;
    }
    if (!code || !state) {
      setError('This page needs the answer Google sends back: start the connection again from the server page.');
      return;
    }
    void (async () => {
      try {
        const { serverId } = await api.google.callback(code, state);
        void qc.invalidateQueries({ queryKey: ['google', serverId] });
        void qc.invalidateQueries({ queryKey: ['notification-stats', serverId] });
        navigate(`/servers/${encodeURIComponent(serverId)}#notify-title`, { replace: true });
      } catch (e) {
        setError((e as Error).message);
      }
    })();
  }, [api, navigate, params, qc]);

  return (
    <div className="mx-auto max-w-lg py-16">
      <h1 className="text-lg font-semibold" style={{ color: 'var(--color-text)' }}>{error ? 'Google not connected' : 'Connecting Google…'}</h1>
      {error ? (
        <>
          <p role="alert" className="mt-2 text-sm" style={{ color: 'var(--color-text-2)' }}>{error}</p>
          <Link to="/servers" className="mt-4 inline-block text-sm hover:underline" style={{ color: 'var(--color-brand-light)' }}>Back to the servers</Link>
        </>
      ) : (
        <p className="mt-2 text-sm" style={{ color: 'var(--color-text-3)' }}>Saving the connection, then back to the server page.</p>
      )}
    </div>
  );
}
